import { z } from 'zod';

import {
  DEFAULT_MAX_FILE_SIZE,
  DEFAULT_MAX_NUMBER_OF_FILES,
  VALID_MIME_TYPES,
  VALID_TOP_LEVEL_TYPES,
} from './constants';

export const fileUploadRestrictionsSchema = z
  .object({
    allowedFileTypes: z
      .array(z.string())
      .optional()
      .transform(filterValidMimeTypes),
    maxFileSize: z.number().positive().optional(),
    maxNumberOfFiles: z.number().int().positive().optional(),
    minFileSize: z.number().positive().optional(),
    minNumberOfFiles: z.number().int().positive().optional(),
    requiredMetaFields: z.array(z.string()).optional(),
  })
  .transform((data) => ({
    ...data,
    maxFileSize: data.maxFileSize ?? DEFAULT_MAX_FILE_SIZE,
    maxNumberOfFiles: data.maxNumberOfFiles ?? DEFAULT_MAX_NUMBER_OF_FILES,
  }));

/**
 * Checks whether a string is a valid MIME type supported by the uploader.
 * Accepts exact types (e.g. `image/png`) or wildcard subtypes (e.g. `image/*`).
 *
 * @param value - The MIME type string to validate.
 * @returns `true` if the value is in VALID_MIME_TYPES or matches a valid top-level type with `*` subtype.
 */
function isValidMimeType(value: string) {
  const normalized = value.toLowerCase().trim();

  if (!normalized.includes('/')) {
    return false;
  }

  const [type, subtype] = normalized.split('/');

  if (!type || !subtype) {
    return false;
  }

  if (VALID_MIME_TYPES.has(normalized)) {
    return true;
  }

  if (subtype === '*' && VALID_TOP_LEVEL_TYPES.has(type)) {
    return true;
  }

  return false;
}

/**
 * Filters an array of MIME type strings, keeping only those that pass
 * `isValidMimeType`. Returns `undefined` if the input is not an array or
 * if no valid types remain after filtering.
 *
 * @param values - Array of MIME type strings to filter.
 * @returns Array of valid MIME types, or `undefined` if none are valid.
 */
function filterValidMimeTypes(values: string[] | undefined) {
  if (!Array.isArray(values)) return undefined;

  const filtered = values.filter(
    (v) => typeof v === 'string' && isValidMimeType(v),
  );

  return filtered.length > 0 ? filtered : undefined;
}
